/**
 * Pure zoom/pan transform math for the shared ImageCanvas (AKL-681). Kept
 * separate from the component so the coordinate accuracy that box overlays
 * depend on is unit-testable.
 *
 * The transform maps a point in the image's *base* layout space (the image
 * rendered at scale 1, top-left origin) to *container* space via
 * `containerPoint = translate + scale * basePoint`, applied with CSS
 * `transform: translate(tx, ty) scale(scale)` and `transform-origin: 0 0`.
 */

export type Transform = {
  scale: number;
  tx: number;
  ty: number;
};

export const MIN_SCALE = 1;
export const MAX_SCALE = 6;

export const identityTransform = (): Transform => ({ scale: 1, tx: 0, ty: 0 });

export const clamp = (value: number, lo: number, hi: number): number =>
  Math.min(hi, Math.max(lo, value));

export const clampScale = (
  scale: number,
  min = MIN_SCALE,
  max = MAX_SCALE,
): number => clamp(scale, min, max);

/**
 * Constrain the translation so the scaled content always covers the container
 * (no empty gutters). At scale 1 the only valid translation is (0, 0).
 * @param t the transform to clamp
 * @param width the container width in px
 * @param height the container height in px
 * @returns the transform with tx/ty clamped
 */
export const clampTranslation = (
  t: Transform,
  width: number,
  height: number,
): Transform => {
  const minTx = width * (1 - t.scale); // negative when scale > 1
  const minTy = height * (1 - t.scale);
  return {
    scale: t.scale,
    tx: clamp(t.tx, minTx, 0),
    ty: clamp(t.ty, minTy, 0),
  };
};

/**
 * Convert a container-space point to the image's base-space point under the
 * current transform. Used by the magnifier to find the pixel under the cursor.
 * @param t the current transform
 * @param cx container-space x (px from container left)
 * @param cy container-space y (px from container top)
 * @returns the corresponding base-space point
 */
export const basePointAt = (
  t: Transform,
  cx: number,
  cy: number,
): { x: number; y: number } => ({
  x: (cx - t.tx) / t.scale,
  y: (cy - t.ty) / t.scale,
});

/**
 * Zoom by `factor` while keeping the container-space point (cx, cy) visually
 * fixed (zoom-to-cursor). The result is scale-clamped and, when container
 * dimensions are supplied, translation-clamped so no gutters appear.
 * @param t the current transform
 * @param factor multiplicative zoom factor (>1 zooms in, <1 zooms out)
 * @param cx container-space x to keep fixed
 * @param cy container-space y to keep fixed
 * @param width container width (for translation clamping); omit to skip
 * @param height container height (for translation clamping); omit to skip
 * @returns the new transform
 */
export const zoomAtPoint = (
  t: Transform,
  factor: number,
  cx: number,
  cy: number,
  width?: number,
  height?: number,
): Transform => {
  const newScale = clampScale(t.scale * factor);
  // Base-space point under the cursor stays fixed: tx' = cx - newScale * baseX.
  const base = basePointAt(t, cx, cy);
  const next: Transform = {
    scale: newScale,
    tx: cx - newScale * base.x,
    ty: cy - newScale * base.y,
  };
  return width != null && height != null
    ? clampTranslation(next, width, height)
    : next;
};

/**
 * Apply a pan delta (in container px) and clamp against the container bounds.
 * @param t the current transform
 * @param dx horizontal delta in px
 * @param dy vertical delta in px
 * @param width container width
 * @param height container height
 * @returns the new, clamped transform
 */
export const panBy = (
  t: Transform,
  dx: number,
  dy: number,
  width: number,
  height: number,
): Transform =>
  clampTranslation(
    { scale: t.scale, tx: t.tx + dx, ty: t.ty + dy },
    width,
    height,
  );
